import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StripeCheckoutButton
 * --------------------
 * Redirects the user to Stripe-hosted Checkout. Supports Apple Pay,
 * Google Pay, cards, and (via customer_creation) saves the card for later.
 *
 * Props:
 *   lookupKey  (string)  required — Stripe price lookup_key (e.g. "deposit_250")
 *   purpose    (string)  "booking_deposit" | "booking_full" | "hold_fee"
 *   bookingId  (string)  optional booking to attribute the payment to
 *   parentToken(string)  optional parent JWT (attributes payment to parent)
 *   amountLabel(string)  optional display label ("$250.00 deposit")
 *   size       (string)  "sm" | "default" | "lg"
 *   fullWidth  (boolean) default false
 */
const StripeCheckoutButton = ({
  lookupKey,
  purpose = "booking_deposit",
  bookingId = "",
  parentToken = "",
  amountLabel = "",
  size = "default",
  fullWidth = false,
  variant = "default",
}) => {
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(true);

  const start = async () => {
    setBusy(true);
    try {
      const headers = parentToken ? { Authorization: `Bearer ${parentToken}` } : {};
      const { data } = await axios.post(
        `${API}/payments/stripe/create-checkout-session`,
        {
          lookup_key: lookupKey,
          origin_url: window.location.origin,
          purpose,
          booking_id: bookingId,
        },
        { headers },
      );
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      data-testid="stripe-checkout-btn"
      onClick={start}
      disabled={busy || !ready}
      size={size}
      variant={variant}
      className={`rounded-full ${fullWidth ? "w-full" : ""}`}
    >
      {busy ? (
        <><Loader2 size={14} className="mr-1.5 animate-spin" /> Redirecting…</>
      ) : (
        <><CreditCard size={14} className="mr-1.5" /> Pay with card {amountLabel && `· ${amountLabel}`}</>
      )}
    </Button>
  );
};

export default StripeCheckoutButton;
